import React from 'react';

import Loadable from 'react-loadable';

import { DefaultExtensionProvider, type ExtensionParams } from '@atlaskit/editor-common/extensions';
import UFOLoadHold from '@atlaskit/react-ufo/load-hold';
import { token } from '@atlaskit/tokens';
import {
	type CustomSitesExtensionProviderProps,
	getExtensionManifest,
	type HubDefaultParameters,
} from '@atlassian/company-hub-common';

import { CALLOUT_BANNER_EXTENSION_KEY, CALLOUT_BANNER_EXTENSION_TYPE } from '../../constants';
import type { CalloutBannerParametersType } from '../../types';

import { i18n } from './messages';
import type { CalloutBannerExtensionProviderProps } from './types';

const CalloutBannerExtension = Loadable({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "@atlaskit-internal_callout-banner-extension" */ '../callout-banner-extension'
			)
		).CalloutBannerExtension,
	loading: () => <UFOLoadHold name="page-topper-extension-loader"></UFOLoadHold>,
});

export const createParameters = ({
	title,
	defaultParameters,
	extensionTitle,
}: {
	defaultParameters?: HubDefaultParameters;
	extensionTitle: string;
	title: string;
}): CalloutBannerParametersType => ({
	extensionTitle,
	title,
	backgroundColor:
		defaultParameters?.backgroundColor ?? token('color.background.accent.green.bolder'),
	titleColor: defaultParameters?.textColor ?? token('color.text'),
	cornerRadius: 16,
	shouldDisplaySearch: true,
});

export const calloutBannerExtensionProvider = async ({
	contentId,
	propsProvider,
	mode,
	CustomSearchComponent,
	defaultParameters,
}: CalloutBannerExtensionProviderProps) => {
	return async ({ editorActions, openConfigPanel, intl }: CustomSitesExtensionProviderProps) => {
		const title = intl.formatMessage(i18n.extensionTitleNew);
		const extensionDescription = intl.formatMessage(i18n.extensionDescription);
		const type = CALLOUT_BANNER_EXTENSION_TYPE;
		const key = CALLOUT_BANNER_EXTENSION_KEY;

		const render =
			async () =>
			({ node }: { node: ExtensionParams<CalloutBannerParametersType> }) => (
				<CalloutBannerExtension
					isInEditor
					contentId={contentId}
					extensionNode={node}
					mode={mode}
					propsProvider={propsProvider}
					CustomSearchComponent={CustomSearchComponent}
				/>
			);

		const icons = {
			// eslint-disable-next-line import/dynamic-import-chunkname
			'48': () =>
				import(
					/* webpackChunkName: "@atlaskit-internal_CalloutBannerExtensionIcon" */
					'./icon/index'
				).then((mod) => mod.CalloutBannerExtensionIcon),
		};

		const parameters = createParameters({
			title: intl.formatMessage(i18n.title),
			defaultParameters,
			extensionTitle: intl.formatMessage(i18n.extensionTitle),
		});

		const update = async () => {
			const selectedNode = editorActions?.getSelectedNode()?.toJSON();
			const selectedLocalId = selectedNode?.attrs?.localId;

			if (!!selectedLocalId && openConfigPanel) {
				openConfigPanel(selectedLocalId);
			}
		};

		return new DefaultExtensionProvider([
			getExtensionManifest<CalloutBannerParametersType>({
				key,
				icons,
				parameters,
				update,
				render,
				type,
				title,
				description: extensionDescription,
				source: undefined,
			}),
		]);
	};
};
